import { useEffect, useState } from "react";
import DashSidebar from "@/components/DashSidebar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Users } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import QuizAnswers from "@/components/QuizAnswers";
import { toast } from "sonner";

const STATUS_COLORS = {
  NEW: "bg-[#9CAF88]/20 text-[#2C4C3B]",
  CONTACTED: "bg-[#E07A5F]/15 text-[#C96B52]",
  SCHEDULED: "bg-blue-100 text-blue-700",
  CONVERTED: "bg-green-100 text-green-700",
  LOST: "bg-gray-200 text-gray-600",
};

export default function PartnerDashboard() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/partner/leads");
      setLeads(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (leadId, status) => {
    try {
      await api.post(`/partner/leads/${leadId}/status`, { status });
      toast.success("Status updated");
      load();
      if (selected?.id === leadId) {
        const { data } = await api.get(`/partner/leads/${leadId}`);
        setSelected(data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    try {
      await api.post(`/leads/${selected.id}/notes`, { note });
      setNote("");
      const { data } = await api.get(`/partner/leads/${selected.id}`);
      setSelected(data);
      toast.success("Note added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F7F5F0]">
      <DashSidebar role="partner" />
      <main className="flex-1 lg:ml-0 ml-64 p-8 md:p-10">
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] mb-1">My Leads</h1>
        <p className="text-[#5C6B62] mb-8">{leads.length} leads assigned to you.</p>

        {leads.length === 0 ? (
          <div className="card-soft p-12 text-center">
            <div className="inline-flex w-14 h-14 rounded-full bg-[#9CAF88]/15 items-center justify-center mb-4">
              <Users className="w-6 h-6 text-[#2C4C3B]" />
            </div>
            <div className="font-display text-xl text-[#1A2E24] mb-1">No leads yet</div>
            <div className="text-sm text-[#5C6B62]">When the admin assigns you a lead, you'll see it here.</div>
          </div>
        ) : (
          <div className="card-soft overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="partner-leads-table">
                <thead className="bg-[#F7F5F0] border-b border-[#E2DFD8]">
                  <tr className="text-left text-xs uppercase tracking-wider text-[#5C6B62]">
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Care type</th>
                    <th className="px-5 py-3 font-medium">Zip</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Created</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2DFD8]">
                  {leads.map((l) => (
                    <tr key={l.id} className="hover:bg-[#F7F5F0]/60" data-testid={`partner-lead-row-${l.id}`}>
                      <td className="px-5 py-4">
                        <div className="font-medium text-[#1A2E24]">{l.first_name}</div>
                        <div className="text-xs text-[#5C6B62]">{l.email}</div>
                      </td>
                      <td className="px-5 py-4 text-sm text-[#1A2E24]">{l.care_type || "—"}</td>
                      <td className="px-5 py-4 text-sm text-[#5C6B62]">{l.zip_code || "—"}</td>
                      <td className="px-5 py-4">
                        <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                          <SelectTrigger className="h-9 w-[140px] rounded-lg border-[#E2DFD8] text-xs" data-testid={`partner-status-${l.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {["CONTACTED", "SCHEDULED", "CONVERTED", "LOST"].map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4 text-xs text-[#5C6B62]">{new Date(l.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => setSelected(l)}
                          className="text-sm text-[#2C4C3B] font-medium hover:underline"
                          data-testid={`partner-view-${l.id}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl text-[#1A2E24]">
                    {selected.first_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-5 mt-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-[#5C6B62]">Email:</span> <span className="text-[#1A2E24]">{selected.email}</span></div>
                    <div><span className="text-[#5C6B62]">Phone:</span> <span className="text-[#1A2E24]">{selected.phone || "—"}</span></div>
                    <div><span className="text-[#5C6B62]">Zip:</span> <span className="text-[#1A2E24]">{selected.zip_code || "—"}</span></div>
                    <div><span className="text-[#5C6B62]">Care type:</span> <span className="text-[#1A2E24]">{selected.care_type || "—"}</span></div>
                    <div><span className="text-[#5C6B62]">Status:</span> <Badge className={STATUS_COLORS[selected.status]}>{selected.status}</Badge></div>
                  </div>

                  <div>
                    <h4 className="font-display text-lg font-semibold text-[#1A2E24] mb-3">Quiz answers</h4>
                    <QuizAnswers answers={selected.answers} careType={selected.care_type} />
                  </div>

                  <div>
                    <h4 className="font-display text-lg font-semibold text-[#1A2E24] mb-2">Notes</h4>
                    <div className="space-y-2 mb-3">
                      {(selected.notes || []).map((n) => (
                        <div key={n.id} className="bg-[#F7F5F0] rounded-xl p-3 text-sm">
                          <div className="text-[#1A2E24]">{n.text}</div>
                          <div className="text-xs text-[#5C6B62] mt-1">{n.by} · {new Date(n.at).toLocaleString()}</div>
                        </div>
                      ))}
                      {(selected.notes || []).length === 0 && <div className="text-sm text-[#5C6B62]">No notes yet.</div>}
                    </div>
                    <div className="flex gap-2">
                      <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a note…"
                        className="rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                        data-testid="partner-note-input"
                      />
                      <Button onClick={addNote} className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl self-end" data-testid="partner-note-submit">
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
